import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';
import { COMPANY_NAME } from '../../constants';
import {
  WHAT_WE_DO_SERVICES,
  WHAT_WE_DO_MEDIA,
} from '../../data/whatWeDo';
import {
  INDUSTRIES,
  INDUSTRIES_MEDIA,
  INDUSTRIES_OVERVIEW_DESCRIPTION,
} from '../../data/industries';
import FullLogo from '../../assets/images/Full_logo.png';

/*
 * SidebarDrawer
 * -------------
 * Zensar-inspired mega-menu drawer. Structure:
 *
 *   ┌─────────────┬──────────────────┬────────────────────────┐
 *   │ Categories  │ Sub-items panel  │ Preview (video + desc) │
 *   │  (col 1)    │      (col 2)     │        (col 3)         │
 *   │             │  reveals on hover│  reveals with col 2    │
 *   └─────────────┴──────────────────┴────────────────────────┘
 *
 * Categories without children (Our Investors, Let's Connect, etc.) still
 * highlight on hover but don't reveal cols 2+3, so the panel gracefully
 * collapses back to just the category rail.
 *
 * On mobile (< md) col 2 is rendered as an accordion directly beneath
 * the active category in col 1 and col 3 is hidden (screen real estate
 * is too tight for a preview + auto-playing video).
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface SidebarSubItem {
  label: string;
  href: string;
  /**
   * Optional per-sub-item preview media + description. When present it
   * overrides the parent category's `preview` while this sub-item is
   * highlighted, so hovering different rows can swap the media / copy
   * in col 3. Sub-items without this fall back to the category preview
   * so we can fill in richer content incrementally.
   */
  preview?: SidebarPreview;
}

interface SidebarPreview {
  mediaUrl: string;
  /** For future image support; today everything is a video. */
  mediaType: 'video' | 'image';
  description: string;
}

interface SidebarCategory {
  label: string;
  /**
   * Where clicking the category header itself navigates. Use "#" for
   * categories that don't have a landing page yet — the click is
   * suppressed so the user isn't dumped on an empty anchor.
   */
  href: string;
  /** Heading shown above the sub-item list in col 2. */
  panelHeading?: string;
  /** Sub-item list. Omit (or empty) to hide col 2 for this category. */
  subItems?: SidebarSubItem[];
  /** Media + description shown in col 3. */
  preview?: SidebarPreview;
}

// ---------------------------------------------------------------------------
// Shared preview media
// ---------------------------------------------------------------------------
// Cover URLs for the "What We Do" services live in the shared
// `whatWeDo` data module (which also drives the individual
// /services/<slug> pages and the Contact form dropdown) so the
// sidebar, the detail pages, and the form stay in perfect sync.
//
// The assets not covered by `WHAT_WE_DO_MEDIA` are the top-level
// category previews (About Us, IstaLabs, Investors, Career, Let's
// Connect) plus every per-industry preview in the "Industries We
// Empower" mega-panel. All hosted on the same CDN as the rest of the
// site's covers.
const ISTATEC_ASSETS_BASE = 'https://istatec-assets.istatec.com';

const ABOUT_US_MEDIA = `${ISTATEC_ASSETS_BASE}/about_us.webm`;
const ISTALABS_MEDIA = `${ISTATEC_ASSETS_BASE}/istalabs_cover.webm`;
const INVESTORS_MEDIA =
  `${ISTATEC_ASSETS_BASE}/istatec_our_investors_cover.webm`;
const CAREER_MEDIA = `${ISTATEC_ASSETS_BASE}/career.webm`;
const LETS_CONNECT_MEDIA = `${ISTATEC_ASSETS_BASE}/lets_connect.webm`;

// Per-industry cover clips are now owned by `src/data/industries.ts`
// (same pattern we already use for the What-We-Do services). The
// sidebar mega-panel derives its sub-items directly from that
// module, so adding an industry is a one-line change in one file
// and the sidebar, the /industries listing, and the
// /industries/<slug> detail pages all update together.

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------
// Only two categories have full mega-panels today ("What We Do" and
// "Our Innovation / Research — IstaLabs"). The others are placeholder
// rows that either link to an existing page or are intentional stubs
// awaiting content (marked with `href: "#"`).
//
// Ordering intent: "About Us" leads because it's the fastest way for
// a new visitor to figure out who we are; "What We Do" follows so
// they can drill into the offerings.
const SIDEBAR_MENU: SidebarCategory[] = [
  {
    label: 'About Us',
    href: '/about',
    // About Us mega-panel: sub-item column carries a single "About
    // Us" link so the panel opens with the same 3-column mega
    // treatment as the IstaLabs / Investors categories, and col 3
    // plays the About Us cover video alongside a short pitch. The
    // single sub-item duplicates the category label on purpose —
    // this mirrors the IstaLabs and Investors pattern where the one
    // in-panel link routes to the same landing page as the category
    // header itself, so wherever the visitor clicks inside the
    // panel they end up on `/about`.
    // `panelHeading` intentionally omitted so the single sub-item
    // reads clean without a redundant title above it.
    subItems: [{ label: 'About Us', href: '/about' }],
    preview: {
      mediaUrl: ABOUT_US_MEDIA,
      mediaType: 'video',
      description:
        "Mid-scale technology partner, headquartered in India and ready to ship globally. We combine senior technical advisors, best-sourced expert engineers, and our IstaLabs innovation practice to help enterprises stay ahead of what's coming next.",
    },
  },
  {
    label: 'What We Do',
    href: '/services',
    // `panelHeading` intentionally omitted — the first list entry
    // ("Explore our services") already reads as the section header.
    subItems: [
      // "Explore our services" is a navigational entry, not a
      // bookable service — it links to the /services list page
      // and has no per-item preview (falls back to the category
      // overview video below).
      { label: 'Explore our services', href: '/services' },
      // All 11 individual services are derived from the shared
      // `WHAT_WE_DO_SERVICES` list so this sidebar, the detail
      // pages (`/services/<id>`), and the Contact-form dropdown
      // are always in lock-step. Adding a new service is a
      // one-line change in `src/data/whatWeDo.ts`.
      ...WHAT_WE_DO_SERVICES.map(
        (s): SidebarSubItem => ({
          label: s.title,
          href: `/services/${s.id}`,
          preview: {
            mediaUrl: s.mediaUrl,
            mediaType: 'video',
            description: s.sidebarTagline,
          },
        }),
      ),
    ],
    preview: {
      // Category fallback — shown while "Explore our services" is the
      // active sub-item (and any future sub-items without their own
      // preview will fall back here too).
      mediaUrl: WHAT_WE_DO_MEDIA.whatWeDoOverview,
      mediaType: 'video',
      description:
        'Explore our full-stack services across applications, data, cloud, security, platforms, marketing, and AI automation — built to help businesses grow, adapt, and lead.',
    },
  },
  {
    label: 'Industries We Empower',
    // Header links to the dedicated /industries listing page (same
    // grammar as "What We Do" → /services). Sub-items below link to
    // each industry's detail page.
    href: '/industries',
    panelHeading: 'Explore By Industry',
    // All 7 industry sub-items are derived from the shared
    // `INDUSTRIES` catalog so this sidebar, the /industries listing,
    // the /industries/<slug> detail pages, and the Contact form's
    // industry pre-fill are always in lock-step. Adding an industry
    // is a one-line change in `src/data/industries.ts`.
    subItems: INDUSTRIES.map(
      (ind): SidebarSubItem => ({
        label: ind.title,
        href: `/industries/${ind.id}`,
        preview: {
          mediaUrl: ind.mediaUrl,
          mediaType: 'video',
          description: ind.sidebarTagline,
        },
      }),
    ),
    preview: {
      // Category fallback — plays while the visitor is still scanning
      // industries before hovering a specific row. Same "Explore by
      // industry" overview clip that the /industries listing hero
      // also uses, so the sidebar → landing-page transition feels
      // continuous.
      mediaUrl: INDUSTRIES_MEDIA.overview,
      mediaType: 'video',
      description: INDUSTRIES_OVERVIEW_DESCRIPTION,
    },
  },
  {
    label: 'Our Innovation / Research — IstaLabs',
    href: '/istalabs',
    // `panelHeading` intentionally omitted — the single sub-item
    // duplicates the category label anyway, so a separate heading
    // would just be visual noise.
    subItems: [
      { label: 'Our Innovation / Research — IstaLabs', href: '/istalabs' },
    ],
    preview: {
      mediaUrl: ISTALABS_MEDIA,
      mediaType: 'video',
      description:
        "IstaLabs, Istatec's innovation hub, drives AI, GenAI, automation, Cloud, NVIDIA tech, and IoT R&D. It incubates CoEs, co-innovates with clients from ideation to prototype, files patents, and delivers solutions for BFSI, retail, and healthcare.",
    },
  },
  {
    label: 'Our Investors',
    href: '/investors',
    // Single sub-item so the mega-panel opens on hover with a
    // preview column — matches the IstaLabs pattern. Heading omitted
    // to avoid the sub-item duplicating an inline title.
    subItems: [{ label: 'Our Investors', href: '/investors' }],
    preview: {
      mediaUrl: INVESTORS_MEDIA,
      mediaType: 'video',
      description:
        "We're proud to have investors who believe in what we're building. Meet the partners backing Istatec's growth — and the funding that's accelerating our roadmap, R&D, and team.",
    },
  },
  {
    label: 'Career',
    href: '/careers',
    // Career mega-panel: same treatment as the other single-link
    // categories (About Us / IstaLabs / Investors / Let's Connect)
    // — one in-panel row that mirrors the category label so any
    // click inside the panel lands on /careers, plus a preview
    // column that plays the "Career" cover video with a short
    // pitch about what it's like to build here.
    // `panelHeading` intentionally omitted so the single sub-item
    // reads clean without a redundant title above it.
    subItems: [{ label: 'Career', href: '/careers' }],
    preview: {
      mediaUrl: CAREER_MEDIA,
      mediaType: 'video',
      description:
        "Work with senior engineers on real problems, ship in weeks not quarters, and stay close to the tech that's actually shaping what's next. If you like tight teams, direct client contact, and continuous learning as part of the job — we should talk.",
    },
  },
  {
    label: "Let's Connect",
    href: '/contact',
    // Let's Connect mega-panel: same treatment as About Us /
    // IstaLabs / Investors — one in-panel link that mirrors the
    // category label so any click inside the panel lands on
    // /contact, plus a preview column that plays the "Let's
    // Connect" cover video and a short invitation to reach out.
    // `panelHeading` intentionally omitted so the single sub-item
    // reads clean without a redundant title above it.
    subItems: [{ label: "Let's Connect", href: '/contact' }],
    preview: {
      mediaUrl: LETS_CONNECT_MEDIA,
      mediaType: 'video',
      description:
        "Whether you're scoping a first engagement, hiring senior engineers, or evaluating vendors — reach out and one of our senior team will get back to you. No sales funnel, no gatekeeping, just a real conversation.",
    },
  },
];

// The drawer is now designed to open with NO panel showing — the
// mega-panel only appears once the visitor hovers a category with
// sub-items. This makes the initial state feel like a lean nav rail
// (three lines → menu → hover to explore) rather than a modal that
// pre-picks a category for you.

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export interface SidebarDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SidebarDrawer: React.FC<SidebarDrawerProps> = ({
  isOpen,
  onClose,
}) => {
  const location = useLocation();
  const panelRef = useRef<HTMLElement | null>(null);

  // Currently-highlighted category (drives cols 2 + 3). `null` means
  // "no panel showing" — the drawer opens in this state and only
  // transitions to a real category once the visitor hovers something
  // with sub-items. We never clear this back to `null` on mouseleave,
  // which keeps the panel sticky — moving diagonally between col 1
  // and col 2 (to click a sub-item) doesn't cause a flicker.
  const [activeLabel, setActiveLabel] = useState<string | null>(null);
  // Currently-highlighted sub-item — visual only for now (all sub-items
  // under a category share the same preview media). Wiring it up now
  // means switching to per-sub-item media later is a one-line change.
  const [activeSubItemLabel, setActiveSubItemLabel] = useState<string | null>(null);

  // Reset both selections every time the drawer transitions from
  // closed → open, so each open starts as a clean nav rail with no
  // panel showing. Without this, a state left over from the previous
  // open (e.g. the user last hovered "What We Do", closed the drawer,
  // then reopened it) would still be active and would look like the
  // drawer opened "with What We Do pre-picked" — the exact bug the
  // hover-only behaviour is designed to avoid.
  //
  // `useLayoutEffect` (not `useEffect`) is deliberate: on reopen, the
  // initial render still carries the stale activeLabel from the last
  // session, which means the aside briefly renders at its expanded
  // ~980px width. If we cleared it in a regular `useEffect` the CSS
  // width transition would fire mid-slide-in and the drawer would
  // look like it's both sliding in AND shrinking. Running the reset
  // synchronously in the layout phase gets us back to 300px before
  // the first paint, so the drawer slides in cleanly at its collapsed
  // width every time.
  useLayoutEffect(() => {
    if (isOpen) {
      setActiveLabel(null);
      setActiveSubItemLabel(null);
    }
  }, [isOpen]);

  // Close whenever the route changes so tapping a nav link takes the
  // user to the page rather than trapping them behind the drawer.
  useEffect(() => {
    onClose();
    // We only care about pathname changes; onClose identity is stable enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Lock body scroll while open — the drawer scrolls internally.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Keep keyboard navigation inside the open drawer and restore the trigger.
  useEffect(() => {
    if (!isOpen) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    panelRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key !== 'Tab') return;
      const controls = Array.from(panelRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex="0"]',
      ) ?? []).filter((el) => el.getClientRects().length > 0);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      previousFocus?.focus();
    };
  }, [isOpen, onClose]);

  // Suppress link navigation for placeholder rows (href = "#") so users
  // don't get punted to an empty anchor. They can still hover to see
  // the panel or skip past to a real link.
  const guardLinkClick = useCallback(
    (href: string) => (e: React.MouseEvent) => {
      if (href === '#') {
        e.preventDefault();
        return;
      }
      onClose();
    },
    [onClose],
  );

  const activeCategory = activeLabel
    ? SIDEBAR_MENU.find((c) => c.label === activeLabel) ?? null
    : null;
  const activeHasPanel = Boolean(activeCategory?.subItems?.length);

  // Resolve which preview should render in col 3. Priority:
  //   1. The currently-hovered sub-item's own `preview` (per-item media)
  //   2. The category's default `preview` (shared fallback)
  //   3. Nothing (no preview block rendered)
  //
  // Kept as a derived value rather than a state slot so the render is
  // always a pure function of activeLabel + activeSubItemLabel.
  const activeSubItemData = activeCategory?.subItems?.find(
    (s) => s.label === activeSubItemLabel,
  );
  const effectivePreview: SidebarPreview | undefined =
    activeSubItemData?.preview ?? activeCategory?.preview ?? undefined;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* ─────────── Backdrop ─────────── */}
          <motion.div
            key="sidebar-backdrop"
            className="fixed inset-0 z-[60] bg-navy-950/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            aria-hidden
          />

          {/* ─────────── Drawer container ─────────── */}
          <motion.aside
            key="sidebar-panel"
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Site navigation"
            className={cn(
              'fixed top-0 left-0 z-[70] h-dvh max-w-full',
              // Mobile: near-full width so we have room for the accordion.
              // `lg:max-w-none` is CRITICAL — without it, the mobile
              // `max-w-[360px]` cap silently clamps the drawer to
                // 360px on every breakpoint, and the desktop
              // `lg:w-[980px]` we compute below has no visible
              // effect (the aside stays 360px wide and its
              // `overflow-hidden` hides cols 2 + 3 entirely). This
              // was the bug that made hover appear to do nothing.
              'w-[92%] max-w-[360px] lg:max-w-none',
              // Desktop: swap between a collapsed rail width (col 1
              // only) and an expanded mega-panel width (col 1 + col 2
              // + col 3) via a strict ternary so ONLY ONE width
              // utility ever lives on the element at a time. Stacking
              // `lg:w-[300px]` and `lg:w-[980px]` in the same class
              // list is unreliable — both utilities compile to the
              // same CSS specificity and source-order in the emitted
              // stylesheet is what would decide the winner. Toggling
              // between them via a ternary sidesteps the whole race,
              // and the `transition-[width]` below animates the
              // in-between growth so it reads as a slide rather than
              // a snap.
              activeCategory && activeHasPanel
                ? 'lg:w-[980px] xl:w-[1120px]'
                : 'lg:w-[300px] xl:w-[340px]',
              'transition-[width] duration-300 ease-out',
              'bg-black text-white shadow-2xl',
              'flex overflow-hidden',
            )}
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 260 }}
          >
            {/* ═══════════ Column 1 — Categories ═══════════
                No divider on the right — the reference design keeps
                the panel visually seamless (columns share the same
                black surface rather than reading as a table).

                Responsive width: 300px in the md–xl range so the whole
                3-column drawer (980px) still fits on a 1024px laptop,
                and 340px at xl+ so the labels have generous breathing
                room on desktop-class monitors. */}
            <div className="w-full lg:w-[300px] xl:w-[340px] shrink-0 flex flex-col">
              {/* Header (close + logo)
                  No border-b either — the reference reads as one
                  clean surface with generous whitespace instead of
                  boxed sections. Extra vertical padding gives the
                  brand mark room to breathe.

                  IMPORTANT: cols 2 + 3 use `pt-[7.5rem]` (120px) which
                  equals `h-24 (96px)` + nav `py-3 (12px)` + row
                  `py-3 (12px)` from col 1 — that's what makes the
                  panel heading land next to the first category text
                  rather than floating up at the ceiling. If you change
                  this h-24, mirror the pt on cols 2 + 3 to keep the
                  baselines aligned. */}
              <div className="flex items-center justify-between px-6 h-24 shrink-0">
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close navigation menu"
                  className={cn(
                    'inline-flex items-center justify-center w-11 h-11 rounded-lg',
                    'text-white/90 hover:text-white hover:bg-white/10',
                    'transition-colors',
                  )}
                >
                  <X className="w-7 h-7" strokeWidth={2.75} />
                </button>
                <Link
                  to="/"
                  onClick={onClose}
                  className="opacity-95 hover:opacity-100 transition-opacity"
                >
                  <img
                    src={FullLogo}
                    alt={COMPANY_NAME}
                    // Bumped from h-8 → h-11 so the mark reads at the
                    // same weight as the category text below it.
                    className="h-11 object-contain"
                  />
                </Link>
              </div>

              {/* Category list — scrollable if it overflows */}
              <nav className="flex-1 min-h-0 overflow-y-auto py-3 px-3">
                {SIDEBAR_MENU.map((cat) => {
                  const isActive = activeLabel === cat.label;
                  const hasChildren = Boolean(cat.subItems?.length);
                  return (
                    <CategoryRow
                      key={cat.label}
                      category={cat}
                      isActive={isActive}
                      hasChildren={hasChildren}
                      onEnter={() => {
                        // Only mutate the "active" (panel-driving) state
                        // when the hovered category actually has a
                        // mega-panel to show. That keeps the preview
                        // sticky — hovering a childless row like
                        // "Industries" or "Let's Connect" leaves the
                        // last preview in place instead of collapsing it,
                        // which feels much less jumpy when a user is
                        // scanning the list.
                        if (!cat.subItems?.length) return;
                        setActiveLabel(cat.label);
                        setActiveSubItemLabel(cat.subItems[0].label);
                      }}
                      onLinkClick={guardLinkClick(cat.href)}
                      onSubItemHover={setActiveSubItemLabel}
                      activeSubItemLabel={activeSubItemLabel}
                      guardSubLinkClick={guardLinkClick}
                    />
                  );
                })}
              </nav>
            </div>

            {/* ═══════════ Columns 2 + 3 — Sub-items + Preview ═══════════
                Only rendered on md+; on mobile we fall back to the
                inline accordion inside col 1. AnimatePresence + a
                keyed motion.div gives us a cross-fade + slide whenever
                the user hovers a different category. */}
            <div className="hidden lg:flex">
              <AnimatePresence mode="wait" initial={false}>
                {activeCategory && activeHasPanel && (
                  <motion.div
                    key={activeCategory.label}
                    className="flex"
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -8 }}
                    transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {/* Col 2 — Sub-item rail
                        No border on the right; the reference design
                        relies on whitespace + type hierarchy rather
                        than dividers to separate sections.

                        `pt-[7.5rem]` (120px) matches col 1's
                        h-24 header + py-3 nav padding + py-3 row
                        padding, so "Explore Our Services" lands at the
                        exact baseline as "What We Do" on its left. */}
                    <div className="w-[320px] xl:w-[360px] shrink-0 pt-[7.5rem] pb-10 px-8 overflow-y-auto">
                      {activeCategory.panelHeading && (
                        // 22px extrabold — anchors the panel visually
                        // and clearly reads as the category label
                        // ("Explore Our Services" / "IstaLabs") at
                        // the wider column width.
                        <p className="text-[22px] font-semibold text-white mb-5">
                          {activeCategory.panelHeading}
                        </p>
                      )}
                      <div className="space-y-0.5">
                        {activeCategory.subItems!.map((sub) => {
                          const subActive = activeSubItemLabel === sub.label;
                          return (
                            <Link
                              key={sub.label}
                              to={sub.href}
                              onClick={guardLinkClick(sub.href)}
                              onMouseEnter={() =>
                                setActiveSubItemLabel(sub.label)
                              }
                              className={cn(
                                // 17px semibold per row + py-3 gives
                                // each option a confident, generous
                                // strike — much closer to the
                                // reference's editorial hierarchy.
                                // Hover lifts opacity to full white
                                // without shifting weight, so there's
                                // no layout jitter.
                                //
                                // `first:pt-0` cancels the top-padding
                                // on the FIRST sub-item only, so its
                                // text baseline lands exactly on the
                                // active category's baseline in col 1
                                // (About Us → About Us, IstaLabs →
                                // IstaLabs, etc.). Without it, the
                                // `py-3` here would stack on top of
                                // the wrapper's `pt-[7.5rem]` and
                                // drop the first row 12px below the
                                // category row — which reads as a
                                // subtle-but-nagging misalignment.
                                // The between-row spacing still comes
                                // from `py-3` on rows 2+.
                                'block px-2 py-3 first:pt-0 rounded-md text-[17px] transition-colors',
                                subActive
                                  ? 'text-white font-bold'
                                  : 'text-white/80 font-semibold hover:text-white',
                              )}
                            >
                              {sub.label}
                            </Link>
                          );
                        })}
                      </div>
                    </div>

                    {/* Col 3 — Preview media + description
                        Same top-padding trick as col 2 so the media
                        frame's top edge aligns with the panel heading
                        and the active category on its left.

                        The preview swaps between sub-item-specific
                        media and the category default via the
                        `effectivePreview` derived above — so hovering
                        e.g. "IT Consultancy & Augmentation" replaces
                        the media + copy without unmounting the whole
                        mega-panel. */}
                    <div className="w-[360px] xl:w-[420px] shrink-0 pt-[7.5rem] pb-10 px-8 overflow-y-auto">
                      {effectivePreview && (
                        <PreviewCard preview={effectivePreview} />
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Row for a single top-level category in column 1. On desktop, hovering
 * simply sets it active. On mobile (no hover), tapping toggles the
 * inline accordion revealing its sub-items (kept identical to the
 * previous drawer behaviour for touch users).
 */
const CategoryRow: React.FC<{
  category: SidebarCategory;
  isActive: boolean;
  hasChildren: boolean;
  onEnter: () => void;
  onLinkClick: (e: React.MouseEvent) => void;
  onSubItemHover: (label: string | null) => void;
  activeSubItemLabel: string | null;
  guardSubLinkClick: (href: string) => (e: React.MouseEvent) => void;
}> = ({
  category,
  isActive,
  hasChildren,
  onEnter,
  onLinkClick,
  onSubItemHover,
  activeSubItemLabel,
  guardSubLinkClick,
}) => {
  // Mobile-only accordion state. Kept separate from `activeLabel` so
  // hover on desktop doesn't force mobile accordions open (the CSS
  // hides the accordion via `lg:hidden` regardless).
  const [mobileExpanded, setMobileExpanded] = useState(false);

  return (
    <div
      // `onMouseEnter` on the wrapper gives us a slightly larger hover
      // target than the link alone, which reduces flicker when the
      // user scans down the list quickly.
      onMouseEnter={onEnter}
      onFocus={onEnter}
      className="mb-0.5"
    >
      <div className="flex items-stretch">
        <Link
          to={category.href}
          onClick={onLinkClick}
          className={cn(
            // Bigger row height + text so the category rail matches
            // the reference's airy, magazine-y hierarchy. Active state
            // relies on font weight + full-strength colour rather than
            // a filled background, which is what makes the reference
            // read as clean instead of tabular.
            //
            // IMPORTANT: This `py-3` combined with the parent nav's
            // `py-3` and header `h-24` gives us the 120px offset that
            // cols 2 + 3 use as `pt-[7.5rem]` to align baselines.
            'flex-1 flex items-center px-3 py-3 rounded-md',
            'text-[18px] transition-colors',
            isActive
              ? // Active: full bold, pure white. Reads as the anchor
                // for the entire right-hand mega-panel.
                'text-white font-bold'
              : // Inactive: still semibold (was medium) at 82% opacity
                // so labels feel confident rather than ghosted, and
                // the hover transition to pure white is a clean
                // brightness lift rather than a weight/tracking shift.
                'text-white/80 font-semibold hover:text-white',
          )}
        >
          <span>{category.label}</span>
          {hasChildren && (
            <ChevronRight
              className={cn(
                'w-4 h-4 ml-auto shrink-0 transition-opacity',
                'hidden lg:inline-block',
                // Only surface the chevron when this category is the
                // active one — matches the reference where only "What
                // We Do" carries the > arrow indicator.
                isActive ? 'opacity-90' : 'opacity-0',
              )}
            />
          )}
        </Link>

        {hasChildren && (
          <button
            type="button"
            onClick={() => setMobileExpanded((v) => !v)}
            aria-label={`${mobileExpanded ? 'Collapse' : 'Expand'} ${category.label}`}
            aria-expanded={mobileExpanded}
            className={cn(
              'lg:hidden ml-1 flex items-center justify-center w-11 rounded-lg',
              'text-white/70 hover:text-white hover:bg-white/10',
              'transition-all',
            )}
          >
            <ChevronDown
              className={cn(
                'w-4 h-4 transition-transform',
                mobileExpanded && 'rotate-180',
              )}
            />
          </button>
        )}
      </div>

      {/* Mobile inline accordion — hidden on md+ where the mega-panel
          renders the same content in columns 2+3 instead. Styled to
          match the desktop rail (borderless, generous line-height,
          bumped type) so touch users get the same airy hierarchy. */}
      <AnimatePresence initial={false}>
        {hasChildren && mobileExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden overflow-hidden"
          >
            <div className="pl-6 pr-2 py-2 space-y-0.5">
              {category.subItems!.map((sub) => (
                <Link
                  key={sub.label}
                  to={sub.href}
                  onClick={guardSubLinkClick(sub.href)}
                  onMouseEnter={() => onSubItemHover(sub.label)}
                  className={cn(
                    // Matches desktop col 2 sub-item weight/size so
                    // touch users get the same editorial hierarchy.
                    'block px-2 py-3 rounded-md text-[17px] transition-colors',
                    activeSubItemLabel === sub.label
                      ? 'text-white font-bold'
                      : 'text-white/80 font-semibold hover:text-white',
                  )}
                >
                  {sub.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * Preview card shown in column 3 of the mega-panel. Renders an
 * autoplaying muted video (matches the reference behaviour of the
 * user-supplied .webm assets) and the category description below.
 * The `key` on the wrapping motion.div in the parent ensures a full
 * remount when switching categories, which lets the browser re-fetch
 * the new source cleanly without a manual `load()` call.
 */
const PreviewCard: React.FC<{ preview: SidebarPreview }> = ({ preview }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Some browsers block autoplay until the element is fully mounted
  // even with `autoPlay muted`. Nudge it once explicitly so the preview
  // doesn't sit on a frozen first frame.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || preview.mediaType !== 'video') return;
    v.muted = true;
    const p = v.play();
    // play() returns a promise on modern browsers; swallow the
    // AbortError that fires if the user navigates away before it
    // resolves.
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, [preview.mediaUrl, preview.mediaType]);

  return (
    <div>
      {/* No border on the media frame — matches the reference's clean,
          borderless treatment. `bg-navy-900` still provides a
          fallback surface if the network hiccups and the video is
          slow to paint its first frame. */}
      <div className="rounded-xl overflow-hidden mb-5 aspect-video bg-navy-900">
        {preview.mediaType === 'video' ? (
          // `key={mediaUrl}` remounts the video element whenever the
          // source URL changes (e.g. hovering a different sub-item
          // that has its own preview). Simpler + more reliable than
          // calling `.load()` on an existing element, and it lets the
          // browser handle the new stream fresh every time.
          <video
            key={preview.mediaUrl}
            ref={videoRef}
            src={preview.mediaUrl}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="w-full h-full object-cover"
          />
        ) : (
          <img
            key={preview.mediaUrl}
            src={preview.mediaUrl}
            alt=""
            className="w-full h-full object-cover"
          />
        )}
      </div>
      {/* 16.5px semibold at 90% opacity reads as fully legible body
          copy — not a footnote. Deliberately closer to a normal
          paragraph tone (rather than the very muted description
          treatment we had before), because on a black surface any
          opacity below ~85% starts to strain the eye. */}
      <p className="text-[16.5px] font-medium text-white/90 leading-relaxed">
        {preview.description}
      </p>
    </div>
  );
};
